import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight } from "@phosphor-icons/react";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", username: "", display_name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        username: form.username,
        display_name: form.display_name || form.username,
      });
      // Send new accounts through the onboarding carousel; existing
      // users coming back fall through to the feed via /login.
      nav("/welcome", { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden grain bg-ink flex items-center justify-center px-5 py-10">
      <div className="absolute inset-0 opacity-50">
        <img src="https://images.unsplash.com/photo-1657450717623-93270c89af8e?fm=jpg&q=85&w=1600&auto=format&fit=crop" alt="Lignum Vitae — Jamaica's national flower" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/75 to-ink" />
      </div>
      <div className="relative z-10 w-full max-w-md crystal-glass rounded-3xl p-8 sm:p-10 animate-fade-up">
        <div className="flex items-center gap-3 mb-3">
          <p className="text-papaya text-xs tracking-[0.3em] uppercase font-bold">Join the Movement</p>
          <div className="jam-stripe h-[3px] flex-1 rounded-full" aria-hidden />
        </div>
        <h1 className="text-cream text-4xl sm:text-5xl font-black tracking-tighter leading-none">Create your<br/>vibe.</h1>
        <p className="text-cream/60 mt-3 text-sm">Join dancers worldwide. Short videos, live sessions, meetings — all in one app.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <Field label="Display Name" value={form.display_name} onChange={set("display_name")} testid="signup-name" />
          <Field label="Username" value={form.username} onChange={set("username")} testid="signup-username" />
          <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="signup-email" />
          <Field label="Password (min 6)" type="password" value={form.password} onChange={set("password")} testid="signup-password" />
          {err && <p data-testid="signup-error" className="text-red-400 text-sm">{err}</p>}
          <button
            data-testid="signup-submit"
            type="submit"
            disabled={loading}
            className="w-full mt-4 py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "Creating…" : <>Sign up <ArrowRight size={16} weight="bold" /></>}
          </button>
        </form>
        <p className="mt-6 text-cream/60 text-sm text-center">
          Already on the wave?{" "}
          <Link to="/login" data-testid="signup-to-login" className="text-papaya font-bold hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="mt-2 w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors"
      />
    </label>
  );
}
